import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {firstValueFrom, from, Observable, Subscription, throwError} from 'rxjs';
import {ProductService} from '@app/service/product.service';
import {ProductCatalogCacheService} from '@app/service/product-catalog-cache.service';
import {SocketIoService} from '@app/service/socket-io.service';
import {EventEnum} from '@app/enums/event.enum';
import {ProductCatalogDelta, ProductsChangedEvent} from '@app/domain/product-catalog';

/** Ventana para juntar avisos seguidos del socket en una sola consulta. */
const EVENT_BATCH_MS = 1000;
/** Retraso aleatorio extra por dispositivo, para que N cajas no consulten en el mismo milisegundo. */
const EVENT_JITTER_MS = 3000;
/** Una recarga total la piden todos los fronts de la empresa: se reparte en este intervalo. */
const FULL_RELOAD_JITTER_MS = 60_000;
/** Primera descarga del catálogo en un navegador sin catálogo local. */
const FIRST_LOAD_JITTER_MS = 10_000;
/** Tras reconectar el socket (p. ej. reinicio de tekiwss) todos reconectan a la vez: se reparte la verificación. */
const RECONNECT_JITTER_MS = 10_000;
/** Verificación de versión de respaldo (con socket conectado / caído), ±20 %. */
const POLL_CONNECTED_MS = 30 * 60_000;
const POLL_DISCONNECTED_MS = 5 * 60_000;
/** Al volver a la pestaña, verificar si la última verificación es más vieja que esto. */
const VISIBLE_RECHECK_MS = 5 * 60_000;
/** Una edición propia ya refrescó el producto: el aviso del socket de ese mismo cambio no lo vuelve a pedir. */
const OWN_REFRESH_TTL_MS = 15_000;
const MAX_IDS_PER_REQUEST = 500;

/**
 * Mantiene al día el catálogo local de productos (búsqueda local) sin saturar el backend:
 *
 *  - La versión la lleva el backend (`company.catalogVersion`), nunca el front.
 *  - Un aviso `productsChanged` por socket trae los ids que cambiaron: se piden solo esos (/by-ids), juntando
 *    avisos seguidos y con un retraso aleatorio por dispositivo.
 *  - Si falta un aviso (salto de versión) se piden las diferencias (/changes). Si el cambio fue masivo, cada
 *    dispositivo descarga el snapshot en un momento aleatorio del siguiente minuto.
 *  - Al reconectar el socket, al volver a la pestaña y cada 5-30 min se verifica la versión (/changes, consulta
 *    indexada que casi siempre responde `sinCambios`).
 *  - Solo una pestaña por navegador sincroniza (Web Locks); las demás reciben los cambios por BroadcastChannel.
 */
@Injectable({
    providedIn: 'root'
})
export class ProductCatalogSyncService implements OnDestroy {

    private idEmpresa: number = null;
    private running = false;
    private leader = false;
    /** Cambia en cada start/stop: las tareas de una sesión anterior se descartan. */
    private generation = 0;

    private queue: Promise<void> = Promise.resolve();
    private subscriptions = new Subscription();
    private lockAbort: AbortController = null;
    private releaseLock: () => void = null;

    private pendingEvents: ProductsChangedEvent[] = [];
    private eventTimer: any = null;
    private fullReloadTimer: any = null;
    private reconcileTimer: any = null;
    private pollTimer: any = null;
    private lastReconcile = 0;
    /** Un snapshot vacío confirmado es válido; no se descarga de nuevo en cada poll. */
    private emptySnapshotVersion: number = null;
    private announceAfterReconcile = false;
    private recentlyRefreshed = new Map<number, number>();
    private visibilityListener: () => void = null;

    constructor(private productService: ProductService,
                private cache: ProductCatalogCacheService,
                private socketIoService: SocketIoService,
                private ngZone: NgZone) {
    }

    get isRunning(): boolean {
        return this.running;
    }

    /** Activa la sincronización del catálogo de la empresa (idempotente). */
    start(idEmpresa: number): void {
        if (!idEmpresa || (this.running && this.idEmpresa === idEmpresa)) {
            return;
        }
        this.stop();
        this.running = true;
        this.idEmpresa = idEmpresa;
        const generation = this.generation;
        this.cache.open(idEmpresa)
            .then(() => {
                if (generation === this.generation) {
                    this.acquireLeadership(generation);
                    if (this.cache.size === 0) {
                        this.cache.requestSync();
                    }
                }
            })
            .catch(error => console.error('[catalogo] No se pudo abrir el catálogo local', error));
    }

    /** Detiene la sincronización (logout, cambio de empresa). Conserva lo guardado. */
    stop(): void {
        this.generation++;
        this.running = false;
        this.leader = false;
        this.emptySnapshotVersion = null;
        this.announceAfterReconcile = false;
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
        this.lockAbort?.abort();
        this.lockAbort = null;
        this.releaseLock?.();
        this.releaseLock = null;
        [this.eventTimer, this.fullReloadTimer, this.reconcileTimer, this.pollTimer].forEach(timer => clearTimeout(timer));
        this.eventTimer = this.fullReloadTimer = this.reconcileTimer = this.pollTimer = null;
        if (this.visibilityListener) {
            document.removeEventListener('visibilitychange', this.visibilityListener);
            this.visibilityListener = null;
        }
        this.pendingEvents = [];
        this.recentlyRefreshed.clear();
        this.cache.close();
        this.idEmpresa = null;
    }

    /** Búsqueda local desactivada para la empresa: detiene y borra el catálogo guardado. */
    disable(idEmpresa: number): void {
        this.stop();
        this.cache.purge(idEmpresa);
    }

    /**
     * Vuelve a pedir productos puntuales (edición o eliminación propia). Funciona en cualquier pestaña y no cambia
     * la versión: el aviso del socket correspondiente la avanza sin volver a descargarlos.
     */
    refreshProducts(ids: number[]): Promise<void> {
        const unique = Array.from(new Set((ids || []).map(Number).filter(id => id > 0)));
        if (!this.running || !unique.length) {
            return Promise.resolve();
        }
        const now = Date.now();
        unique.forEach(id => this.recentlyRefreshed.set(id, now));
        return this.enqueue(() => this.fetchAndApply(unique));
    }

    /** Aplica en local un cambio ya confirmado por el backend (p. ej. orden de favoritos) y luego lo verifica. */
    patchProducts(changes: Array<{id: number; [field: string]: any}>): Promise<void> {
        if (!this.running || !changes?.length) {
            return Promise.resolve();
        }
        return this.cache.patch(changes).then(() => this.refreshProducts(changes.map(change => change.id)));
    }

    /** Descarga manual del catálogo completo (botón "Sincronizar"). */
    forceFullReload(): Observable<{count: number}> {
        if (!this.running) {
            return throwError(() => new Error('La búsqueda local de productos no está activa'));
        }
        // A diferencia de enqueue, el error llega al caller para mostrarlo.
        const result = this.queue.then(() => this.loadSnapshot(true));
        this.queue = result.catch(error => console.warn('[catalogo] Error en la descarga manual', error));
        return from(result.then(() => ({count: this.cache.size})));
    }

    ngOnDestroy(): void {
        this.stop();
    }

    // ---- Liderazgo entre pestañas ----------------------------------------------------------------------------

    private acquireLeadership(generation: number): void {
        const locks: any = (navigator as any)?.locks;
        if (!locks?.request || typeof AbortController === 'undefined') {
            this.becomeLeader(generation);
            return;
        }
        this.lockAbort = new AbortController();
        locks.request(`teki-catalog-sync-${this.idEmpresa}`, {signal: this.lockAbort.signal}, () => {
            if (generation !== this.generation) {
                return undefined;
            }
            this.becomeLeader(generation);
            // Se mantiene el lock mientras la pestaña viva; al cerrarla, la siguiente en espera toma el relevo.
            return new Promise<void>(resolve => this.releaseLock = resolve);
        }).catch((error: any) => {
            if (error?.name !== 'AbortError' && generation === this.generation && !this.leader) {
                this.becomeLeader(generation);
            }
        });
    }

    private becomeLeader(generation: number): void {
        if (generation !== this.generation || this.leader) {
            return;
        }
        this.leader = true;
        this.subscriptions.add(this.cache.changed$.subscribe(() => {
            if (this.cache.size > 0) {
                this.emptySnapshotVersion = null;
            }
        }));
        this.subscriptions.add(this.cache.syncRequested$.subscribe(() => {
            this.announceAfterReconcile = true;
            this.scheduleReconcile(random(EVENT_JITTER_MS));
        }));
        this.subscriptions.add(this.socketIoService.onEvent(EventEnum.PRODUCTS_CHANGED)
            .subscribe(data => this.onProductsChanged(data)));
        this.subscriptions.add(this.socketIoService.onConnect()
            .subscribe(() => this.scheduleReconcile(random(RECONNECT_JITTER_MS))));
        this.visibilityListener = () => {
            if (document.visibilityState === 'visible' && Date.now() - this.lastReconcile > VISIBLE_RECHECK_MS) {
                this.scheduleReconcile(random(3000));
            }
        };
        document.addEventListener('visibilitychange', this.visibilityListener);
        // Primera carga sin catálogo (p. ej. todos los que recargan tras un despliegue): se reparte en unos segundos
        // para no pedir el catálogo completo a la vez; mientras tanto la búsqueda usa HTTP como siempre.
        this.scheduleReconcile(this.cache.currentVersion == null ? random(FIRST_LOAD_JITTER_MS) : 0);
        this.schedulePoll();
    }

    // ---- Avisos del socket -----------------------------------------------------------------------------------

    private onProductsChanged(data: any): void {
        const event: ProductsChangedEvent = data && data.value !== undefined ? data.value : data;
        if (!event || Number(event.idEmpresa) !== Number(this.idEmpresa) || !Number.isFinite(Number(event.version))) {
            return;
        }
        this.pendingEvents.push({...event, version: Number(event.version)});
        if (!this.eventTimer) {
            this.eventTimer = this.later(() => {
                this.eventTimer = null;
                const events = this.pendingEvents;
                this.pendingEvents = [];
                this.enqueue(() => this.applyEvents(events));
            }, EVENT_BATCH_MS + random(EVENT_JITTER_MS));
        }
    }

    private async applyEvents(events: ProductsChangedEvent[]): Promise<void> {
        const generation = this.generation;
        const local = this.cache.currentVersion;
        if (local == null || this.cache.size === 0) {
            return this.reconcile();
        }
        const pending = events.filter(event => event.version > local).sort((a, b) => a.version - b.version);
        if (!pending.length) {
            return;
        }
        if (pending.some(event => event.recargaTotal)) {
            this.scheduleFullReload();
            return;
        }
        const versions = Array.from(new Set(pending.map(event => event.version)));
        const contiguous = versions.every((version, index) => version === local + 1 + index);
        if (!contiguous) {
            // Se perdió al menos un aviso: pedir todo lo que cambió desde la versión local.
            return this.reconcile();
        }
        const now = Date.now();
        const ids = Array.from(new Set(pending.reduce((all, event) => all.concat(event.ids || []), [] as number[]).map(Number)))
            .filter(id => now - (this.recentlyRefreshed.get(id) || 0) > OWN_REFRESH_TTL_MS);
        if (ids.length) {
            await this.fetchAndApply(ids);
        }
        if (generation !== this.generation) {
            return;
        }
        await this.cache.setVersion(versions[versions.length - 1]);
    }

    // ---- Sincronización --------------------------------------------------------------------------------------

    /** Se pone al día con /changes (o snapshot si no hay versión local o el backend lo pide). */
    private async reconcile(): Promise<void> {
        const generation = this.generation;
        await this.reconcileCatalog();
        if (generation === this.generation && this.announceAfterReconcile) {
            this.announceAfterReconcile = false;
            this.cache.announceStoredCatalog();
        }
    }

    private async reconcileCatalog(): Promise<void> {
        const generation = this.generation;
        this.lastReconcile = Date.now();
        const hasData = await this.cache.hasData();
        if (generation !== this.generation) {
            return;
        }
        const local = this.cache.currentVersion;
        if (hasData) {
            this.emptySnapshotVersion = null;
        }
        if (local == null || (!hasData && this.emptySnapshotVersion !== local)) {
            return this.loadSnapshot(true);
        }
        const delta = await firstValueFrom(this.productService.findCatalogChanges(local));
        if (generation !== this.generation) {
            return;
        }
        if (delta.recargaTotal) {
            return this.loadSnapshot(false);
        }
        if (delta.sinCambios) {
            return;
        }
        await this.apply(delta);
        if (generation !== this.generation) {
            return;
        }
        await this.cache.setVersion(delta.version);
    }

    private async loadSnapshot(force: boolean): Promise<void> {
        const generation = this.generation;
        this.lastReconcile = Date.now();
        const version = force || this.cache.size === 0 ? null : this.cache.currentVersion;
        const delta = await firstValueFrom(this.productService.findCatalogSnapshot(version));
        if (generation !== this.generation) {
            return;
        }
        if (delta.sinCambios) {
            return;
        }
        await this.cache.replaceAll(delta.productos || [], delta.version);
        if (generation === this.generation) {
            this.emptySnapshotVersion = this.cache.size === 0 ? delta.version : null;
        }
    }

    private async fetchAndApply(ids: number[]): Promise<void> {
        const generation = this.generation;
        for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
            const delta = await firstValueFrom(this.productService.findCatalogByIds(ids.slice(i, i + MAX_IDS_PER_REQUEST)));
            if (generation !== this.generation) {
                return;
            }
            await this.apply(delta);
            if (generation !== this.generation) {
                return;
            }
        }
    }

    private async apply(delta: ProductCatalogDelta): Promise<void> {
        const generation = this.generation;
        await this.cache.upsert(delta.productos || []);
        if (generation === this.generation) {
            await this.cache.remove(delta.eliminados || []);
        }
    }

    // ---- Programación ----------------------------------------------------------------------------------------

    private scheduleReconcile(delayMs: number): void {
        if (this.reconcileTimer) {
            return;
        }
        this.reconcileTimer = this.later(() => {
            this.reconcileTimer = null;
            this.enqueue(() => this.reconcile());
        }, delayMs);
    }

    private scheduleFullReload(): void {
        if (this.fullReloadTimer) {
            return;
        }
        this.fullReloadTimer = this.later(() => {
            this.fullReloadTimer = null;
            this.enqueue(() => this.reconcile());
        }, random(FULL_RELOAD_JITTER_MS));
    }

    private schedulePoll(): void {
        const base = this.socketIoService.connected ? POLL_CONNECTED_MS : POLL_DISCONNECTED_MS;
        this.pollTimer = this.later(() => {
            this.enqueue(() => this.reconcile());
            this.schedulePoll();
        }, base * (0.8 + Math.random() * 0.4));
    }

    /** Ejecuta las tareas de una en una; un error nunca detiene la sincronización. */
    private enqueue(task: () => Promise<void>): Promise<void> {
        const generation = this.generation;
        this.queue = this.queue
            .then(() => generation === this.generation ? task() : undefined)
            .catch(error => console.warn('[catalogo] Error sincronizando el catálogo local', error));
        return this.queue;
    }

    /** Timers fuera de Angular: no disparan detección de cambios. */
    private later(fn: () => void, delayMs: number): any {
        const generation = this.generation;
        return this.ngZone.runOutsideAngular(() => setTimeout(() => {
            if (generation === this.generation) {
                fn();
            }
        }, delayMs));
    }
}

function random(maxMs: number): number {
    return Math.floor(Math.random() * maxMs);
}
